import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Trophy, Users, ClipboardX } from "lucide-react";
import WidgetExport from "./WidgetExport";

/** Agents & Teams tab — productivity ranking + team (language pool) comparison. */
export default function AgentsTab({ data, drill }) {
  const agents = data?.agents || [];
  const teams = data?.teams || [];
  const incomplete = data?.incomplete || {};
  return (
    <div className="space-y-4" data-testid="dash-tab-agents">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {teams.map((t) => (
          <Card key={t.team} className="p-3" data-testid={`team-card-${t.team.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">{t.team}</span>
              <Users className="w-4 h-4 text-indigo-600" strokeWidth={1.75} />
            </div>
            <div className="text-xl font-semibold text-slate-900">{t.completed}<span className="text-xs text-slate-400 font-normal"> / {t.assigned} captured</span></div>
            <div className="text-[10px] text-slate-400">{t.agents} agent{t.agents === 1 ? "" : "s"} · {t.capture_rate}% capture · {t.calls} calls</div>
          </Card>
        ))}
        {teams.length === 0 && <div className="text-xs text-slate-400 col-span-full">No assigned leads yet.</div>}
      </div>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" /> Agent Performance & Ranking
            </CardTitle>
            <p className="text-[10px] text-slate-400">Ranked by data captured, then capture rate. AHT shows N/A until Genesys call durations flow in. Click a row to open that agent's leads.</p>
          </div>
          <WidgetExport title="Agent Performance"
                        headers={["Rank", "Agent", "Email", "Team", "Assigned", "Captured", "Capture %", "Calls", "AHT", "Callbacks", "Wrong Numbers", "Refused", "Overdue"]}
                        getRows={() => agents.map((a) => [a.rank, a.name, a.assignee, a.team, a.assigned, a.completed, a.capture_rate, a.calls, a.aht ?? "N/A", a.cb, a.wn, a.rf, a.overdue])} />
        </CardHeader>
        <CardContent className="p-2 overflow-x-auto" data-testid="agents-table">
          <table className="w-full text-xs min-w-[760px]">
            <thead className="text-[10px] uppercase text-slate-400">
              <tr>
                <th className="text-left px-2 py-1">#</th><th className="text-left px-2">Agent</th>
                <th className="text-left px-2">Team</th>
                <th className="text-right px-2">Assigned</th><th className="text-right px-2">Captured</th>
                <th className="text-right px-2">Capture %</th><th className="text-right px-2">Calls</th>
                <th className="text-right px-2">AHT</th>
                <th className="text-right px-2">CB</th><th className="text-right px-2">WN</th>
                <th className="text-right px-2">RF</th><th className="text-right px-2">Overdue</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.assignee} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => drill({ assignee: a.assignee })} data-testid={`agent-row-${a.assignee}`}>
                  <td className="px-2 py-1.5">
                    <Badge variant="outline" className={`text-[10px] font-mono ${a.rank === 1 ? "border-amber-300 text-amber-700 bg-amber-50" : ""}`}>{a.rank}</Badge>
                  </td>
                  <td className="px-2">
                    <div className="font-medium text-slate-800">{a.name}</div>
                    <div className="text-[10px] text-slate-400">{a.assignee}</div>
                  </td>
                  <td className="px-2 text-slate-500">{a.team}</td>
                  <td className="text-right px-2 font-mono">{a.assigned}</td>
                  <td className="text-right px-2 font-mono text-emerald-700">{a.completed}</td>
                  <td className="text-right px-2 font-mono">{a.capture_rate}%</td>
                  <td className="text-right px-2 font-mono">{a.calls}</td>
                  <td className="text-right px-2 font-mono text-slate-400">{a.aht ?? "N/A"}</td>
                  <td className="text-right px-2 font-mono text-sky-700">{a.cb}</td>
                  <td className="text-right px-2 font-mono text-red-700">{a.wn}</td>
                  <td className="text-right px-2 font-mono text-rose-700">{a.rf}</td>
                  <td className="text-right px-2 font-mono text-amber-700">{a.overdue}</td>
                </tr>
              ))}
              {agents.length === 0 && (
                <tr><td colSpan={12} className="text-center text-slate-400 py-4">No agent activity yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card className={incomplete.total > 0 ? "border-rose-200" : ""} data-testid="team-incomplete-card">
        <CardHeader className="pb-2 flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <ClipboardX className="w-4 h-4 text-rose-600" /> Incomplete Captures — Team
              {incomplete.total > 0 && (
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200"
                      data-testid="team-incomplete-total">
                  {incomplete.total}
                </span>
              )}
            </CardTitle>
            <p className="text-[10px] text-slate-400">
              Calls handled by an agent with no disposition recorded (e.g. wrap-up timeouts). Agents get a daily nudge until closed out.
            </p>
          </div>
          <WidgetExport title="Team Incomplete Captures"
                        headers={["Agent", "Incomplete"]}
                        getRows={() => (incomplete.by_agent || []).map((r) => [r.agent, r.count])} />
        </CardHeader>
        <CardContent className="p-2" data-testid="team-incomplete-body">
          {incomplete.total === 0 ? (
            <div className="text-xs text-slate-400 p-2">None — every handled call has a disposition.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <div className="text-[10px] uppercase text-slate-400 px-1">By agent</div>
                {(incomplete.by_agent || []).map((r) => (
                  <button key={r.agent} type="button" onClick={() => drill({ assignee: r.agent })}
                          className="w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-rose-50/50 text-xs"
                          data-testid={`team-incomplete-agent-${r.agent}`}>
                    <span className="text-slate-700 truncate">{r.agent}</span>
                    <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px] font-mono">{r.count}</Badge>
                  </button>
                ))}
              </div>
              <div className="space-y-1">
                <div className="text-[10px] uppercase text-slate-400 px-1">Most recent</div>
                <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                  {(incomplete.recent || []).map((l) => (
                    <Link key={l.id} to={`/leads/${l.id}`}
                          className="flex items-center justify-between px-2 py-1.5 hover:bg-slate-50 text-xs"
                          data-testid={`team-incomplete-lead-${l.customer_id}`}>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 truncate">{l.customer_name}</div>
                        <div className="text-[10px] text-slate-400 font-mono">{l.customer_id} · {l.assigned_to}</div>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        <div className="font-mono text-[11px] text-slate-700">{l.attempts} dial{l.attempts === 1 ? "" : "s"}</div>
                        <div className="text-[10px] text-slate-400">
                          {l.last_dialed_at ? String(l.last_dialed_at).slice(0, 16).replace("T", " ") : "—"}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
